import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardCaseCard from './DashboardCaseCard';
import { Appointment, Case } from '../../types';
import {
  buildNextAppointmentByCaseId,
  buildSessionAppointmentsByCaseId,
  formatTimeAgo,
  getCalendarDatePath,
} from './dashboardUtils';
import { t } from '../../utils/translations';
import { useTimedPulse } from '../../hooks/useTimedPulse';

const DISPLAY_LIMIT = 2;

interface RecentActiveCasesProps {
  cases: Case[];
  sessionReportCounts: Record<string, number>;
  upcomingAppointments: Appointment[];
  loading?: boolean;
  onAddReport: (caseItem: Case) => void;
  onAddNote: (caseItem: Case) => void;
  onSchedule: (caseItem: Case, sessionNumber?: number) => void;
  onPrepare?: (caseItem: Case, sessionNumber: number, appointment: Appointment) => void;
}

const RecentActiveCases: React.FC<RecentActiveCasesProps> = ({
  cases,
  sessionReportCounts,
  upcomingAppointments,
  loading,
  onAddReport,
  onAddNote,
  onSchedule,
  onPrepare,
}) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const pulseAppointment = useTimedPulse(5000, !loading);

  const nextAppointmentByCaseId = useMemo(
    () => buildNextAppointmentByCaseId(upcomingAppointments),
    [upcomingAppointments]
  );

  const sessionAppointmentsByCaseId = useMemo(
    () => buildSessionAppointmentsByCaseId(upcomingAppointments),
    [upcomingAppointments]
  );

  const visibleCases = showAll ? cases : cases.slice(0, DISPLAY_LIMIT);
  const hasMore = cases.length > DISPLAY_LIMIT;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900">
          {t.dashboard.recentActiveCases}
        </h2>
      </div>

      {loading ? (
        <div className="flex flex-col gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-xl border border-slate-200 bg-slate-50" />
          ))}
        </div>
      ) : cases.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">
          Nu există cazuri active momentan.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {visibleCases.map((caseItem) => {
            const nextAppointment = nextAppointmentByCaseId[caseItem.id];
            return (
              <DashboardCaseCard
                key={caseItem.id}
                caseItem={caseItem}
                reportCount={sessionReportCounts[caseItem.id] ?? 0}
                lastActivityLabel={formatTimeAgo(
                  caseItem.lastMeetingDate ?? caseItem.updatedAt
                )}
                nextAppointment={nextAppointment}
                appointmentBySession={sessionAppointmentsByCaseId[caseItem.id]}
                pulseAppointment={pulseAppointment}
                onView={() => navigate(`/cases?caseId=${caseItem.id}`)}
                onAddNote={() => onAddNote(caseItem)}
                onAddReport={() => onAddReport(caseItem)}
                onSchedule={(sessionNumber) => onSchedule(caseItem, sessionNumber)}
                onPrepare={
                  onPrepare
                    ? (session, appointment) => onPrepare(caseItem, session, appointment)
                    : undefined
                }
                onOpenReport={(session) =>
                  navigate(`/session-reports?caseId=${caseItem.id}&session=${session}`)
                }
                onOpenAppointment={
                  nextAppointment
                    ? () => navigate(getCalendarDatePath(nextAppointment.date))
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      {!loading && cases.length > 0 && (
        <div className="mt-3 flex flex-col gap-2 min-[450px]:flex-row min-[450px]:items-center">
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="self-start text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              {showAll ? t.common.showLess : t.common.showMore}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/cases?status=active')}
            className="self-start text-sm font-medium text-brand-600 hover:text-brand-700 min-[450px]:ml-auto"
          >
            {t.dashboard.viewAllActiveCases} →
          </button>
        </div>
      )}
    </section>
  );
};

export default RecentActiveCases;
